import type { AudioEvent, AudioPlayOpts, AudioSpeedMode, AudioBusId, AudioDropReason } from "../ports/types";
import type { AudioEventMapping, SoundAsset } from "../banks/types";

/** Every refusal, by reason (the dev snapshot reads them). */
export type DebugCounters = Record<AudioDropReason, number>;
import { ClusterRateLimiter } from "../rate/ClusterRateLimiter";
import { resolveBus, resolveGain } from "./soundResolution";

/** What a one-shot needs from the engine, once the policy has let it through. */
export interface OneShotPlan {
  bus: AudioBusId;
  priority: number;
  protectFromSteal: boolean;
  stopOnSlam: boolean;
  playbackRate: number;
  gain: number | undefined;
}

export type AudioDecision = { kind: "play" } | { kind: "skip"; reason: "mode_skip_nonessential" | "super_skip_isSkippable" };

/**
 * Whether and how a sound plays — pure decisions over the event mapping, the speed mode, the cluster
 * rate limits and the play options. Every refusal is counted in `drops` (the dev snapshot reads
 * them). Nothing here touches the engine: the service looks assets and buffers up and plays.
 */
export class AudioPlayPolicy {
  public readonly drops: DebugCounters = {
    rate_limit: 0,
    cluster_coalesce: 0,
    voice_steal: 0,
    super_skip_isSkippable: 0,
    mode_skip_nonessential: 0,
    missing_sound: 0,
  };
  private mode: AudioSpeedMode = "normal";
  private readonly limiter: ClusterRateLimiter;
  private readonly random: () => number;
  private readonly scheduleFlush: ((flush: () => void) => void) | null;
  private readonly playRepresentative: ((clusterGroup: string) => void) | null;
  private flushScheduled = false;

  public constructor(deps: {
    mapping: AudioEventMapping;
    /** The event ids turbo and super skip unless essential (`GameAudioOverrides.turboSkipEventIds`); absent = none. */
    turboSkipEventIds?: Iterable<string>;
    limiter?: ClusterRateLimiter;
    random?: () => number;
    /**
     * Runs a burst's flush at the end of the current moment (the service passes `queueMicrotask`).
     * Until 2026-09-26 a burst was released by the NEXT sound event only — seconds later, or with the
     * next spin when a burst ended a round (review finding 53).
     */
    scheduleFlush?: (flush: () => void) => void;
    /** Plays a released group's one representative sound. */
    playRepresentative?: (clusterGroup: string) => void;
  }) {
    this.mapping = deps.mapping;
    this.turboSkipEventIds = new Set(deps.turboSkipEventIds ?? []);
    this.limiter = deps.limiter ?? new ClusterRateLimiter(18, 10);
    this.random = deps.random ?? Math.random;
    this.scheduleFlush = deps.scheduleFlush ?? null;
    this.playRepresentative = deps.playRepresentative ?? null;
  }

  private readonly mapping: AudioEventMapping;
  private readonly turboSkipEventIds: ReadonlySet<string>;

  public setSpeedMode(mode: AudioSpeedMode): void {
    this.mode = mode;
  }

  public resolveSoundId(event: AudioEvent): string | null {
    return this.mapping[event.id] ?? null;
  }

  /** Turbo / super skip the title's turbo-skipped events unless essential; super also skips a skippable action's sounds. */
  public decideEvent(input: { event: AudioEvent; essential: boolean; actionIsSkippableInSuper: boolean }): AudioDecision {
    if (this.mode === "super" && input.actionIsSkippableInSuper) {
      this.drops.super_skip_isSkippable++;
      return { kind: "skip", reason: "super_skip_isSkippable" };
    }
    if ((this.mode === "turbo" || this.mode === "super") && !input.essential && this.turboSkipEventIds.has(input.event.id)) {
      this.drops.mode_skip_nonessential++;
      return { kind: "skip", reason: "mode_skip_nonessential" };
    }
    return { kind: "play" };
  }

  public countMissingSound(): void {
    this.drops.missing_sound++;
  }

  /** The engine stopped a playing voice to make room over its voice cap. */
  public countStolenVoice(): void {
    this.drops.voice_steal++;
  }

  /**
   * A one-shot's plan, or null when it must not play: its bus is disabled (silent), or it joins its
   * cluster's burst (counted). `bypassCluster` is for the coalesced representatives `flushClusters`
   * releases.
   */
  public planOneShot(input: { asset: SoundAsset; extra?: AudioPlayOpts; bypassCluster?: boolean; isBusEnabled: (bus: AudioBusId) => boolean }): OneShotPlan | null {
    const bus = resolveBus(input.asset.category);
    if (!input.isBusEnabled(bus)) return null;

    const opts: AudioPlayOpts = { ...(input.asset.defaultOpts ?? {}), ...(input.extra ?? {}) };
    if (!input.bypassCluster && opts.clusterGroup) {
      this.limiter.request(opts.clusterGroup);
      this.drops.cluster_coalesce++;
      this.requestFlush();
      return null;
    }

    return {
      bus,
      priority: opts.priority ?? 0,
      protectFromSteal: !!opts.protectFromSteal,
      stopOnSlam: !!opts.stopOnSlam,
      playbackRate: this.resolvePlaybackRate(opts),
      gain: resolveGain(opts.gain),
    };
  }

  /**
   * The coalesced cluster groups allowed through this frame, each played once as its representative
   * sound. A group over the per-second limits plays nothing and is counted as `rate_limit`; until
   * 2026-09-26 those were dropped uncounted and `rate_limit` was counted only on a path nothing took
   * (review finding 30).
   */
  public flushClusters(): string[] {
    const { allowed, refused } = this.limiter.flush();
    this.drops.rate_limit += refused;
    return allowed;
  }

  /** One flush per burst: the first coalesced sound schedules it, the rest join it. */
  private requestFlush(): void {
    if (this.flushScheduled || !this.scheduleFlush) return;
    this.flushScheduled = true;
    this.scheduleFlush(() => {
      this.flushScheduled = false;
      for (const group of this.flushClusters()) this.playRepresentative?.(group);
    });
  }

  private resolvePlaybackRate(opts: AudioPlayOpts): number {
    // An explicit playbackRate wins over a random range.
    if (typeof opts.playbackRate === "number" && Number.isFinite(opts.playbackRate) && opts.playbackRate > 0) {
      return opts.playbackRate;
    }
    const min = opts.randomPlaybackRateMin;
    const max = opts.randomPlaybackRateMax;
    if (typeof min !== "number" || typeof max !== "number") return 1;
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    if (!Number.isFinite(low) || !Number.isFinite(high) || low <= 0 || high <= 0) return 1;
    if (low === high) return low;
    return low + this.random() * (high - low);
  }
}
